// VK ID 新版登录实现（不使用 SDK，前端交换 code）
// 文档：https://id.vk.com/about/business/go/docs/en/vkid/latest/vk-id/connection/start-integration/how-auth-works/auth-flow-web
import {
  storage,
  vkCodeVerifierStorageKey,
  vkStateStorageKey,
} from '@/utils/storage'

const VK_CODE_VERIFIER_SESSION_KEY = 'vk_code_verifier'
const VK_STATE_SESSION_KEY = 'vk_state'

function setSessionItem(key, value) {
  try {
    if (
      typeof window !== 'undefined' &&
      window.sessionStorage &&
      typeof sessionStorage.setItem === 'function'
    ) {
      sessionStorage.setItem(key, value)
    }
  } catch (e) {
    // ignore
  }
}

function getSessionItem(key) {
  try {
    if (
      typeof window !== 'undefined' &&
      window.sessionStorage &&
      typeof sessionStorage.getItem === 'function'
    ) {
      return sessionStorage.getItem(key) || ''
    }
  } catch (e) {
    // ignore
  }
  return ''
}

function removeSessionItem(key) {
  try {
    if (
      typeof window !== 'undefined' &&
      window.sessionStorage &&
      typeof sessionStorage.removeItem === 'function'
    ) {
      sessionStorage.removeItem(key)
    }
  } catch (e) {
    // ignore
  }
}

function setVkAuthCache({ codeVerifier = '', state = '' } = {}) {
  if (codeVerifier) {
    setSessionItem(VK_CODE_VERIFIER_SESSION_KEY, codeVerifier)
    storage.set(vkCodeVerifierStorageKey, codeVerifier)
  }
  if (state) {
    setSessionItem(VK_STATE_SESSION_KEY, state)
    storage.set(vkStateStorageKey, state)
  }
}

function getVkAuthCache() {
  const state =
    getSessionItem(VK_STATE_SESSION_KEY) || storage.get(vkStateStorageKey) || ''
  const codeVerifier =
    getSessionItem(VK_CODE_VERIFIER_SESSION_KEY) ||
    storage.get(vkCodeVerifierStorageKey) ||
    ''
  return {
    state,
    codeVerifier,
  }
}

function removeVkStateCache() {
  removeSessionItem(VK_STATE_SESSION_KEY)
  storage.remove(vkStateStorageKey)
}

export function clearVkVerifierCache() {
  removeSessionItem(VK_CODE_VERIFIER_SESSION_KEY)
  storage.remove(vkCodeVerifierStorageKey)
}

function clearVkAuthCache() {
  removeVkStateCache()
  clearVkVerifierCache()
}

// 生成随机字符串（用于 code_verifier 和 state）
function generateRandomString(length) {
  const charset =
    'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789_-'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += charset.charAt(Math.floor(Math.random() * charset.length))
  }
  return result
}

// 生成 code_challenge (SHA256 + Base64URL)
async function generateCodeChallenge(verifier) {
  const encoder = new TextEncoder()
  const data = encoder.encode(verifier)
  const digest = await crypto.subtle.digest('SHA-256', data)

  // Base64URL 编码（将 + 替换为 -，/ 替换为 _，移除 =）
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

/**
 * 初始化 VK 登录（新版 VK ID）
 * @param {Object} options - 配置选项
 * @param {boolean} options.gaTrack - 是否启用 GA 追踪（默认 true）
 */
export async function initVK(options = {}) {
  const { gaTrack = true } = options || {}

  try {
    // Step 1: 生成 PKCE 参数
    const codeVerifier = generateRandomString(128) // 43-128 字符，使用 128 确保安全
    const codeChallenge = await generateCodeChallenge(codeVerifier)
    const state = generateRandomString(32) // 至少 32 字节

    // Step 2: 保存 code_verifier 和 state 到 sessionStorage（用于后续验证和交换 token）
    // 使用项目已有的 sessionStorage 工具
    setVkAuthCache({ codeVerifier, state })

    // Step 3: 构建授权 URL
    const redirectUri = `${window.location.origin}/ru/loginCode`
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: '54466135', // VK 应用 ID
      scope: 'email', // 需要的权限
      redirect_uri: redirectUri,
      state: state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    })

    // 保留原有的 gaTrack 参数支持
    if (!gaTrack) {
      params.append('gaTrack', 'false')
    }

    const authUrl = `https://id.vk.com/authorize?${params.toString()}`

    // Step 4: 跳转到授权页面（同标签页重定向）
    window.location.href = authUrl
  } catch (error) {
    // eslint-disable-next-line
    console.error('VK login initialization failed:', error)
    throw error
  }
}

/**
 * 处理 VK 登录回调，验证 state 并提取 PKCE 参数
 * @param {Object} queryParams - URL 查询参数对象（通常来自 $route.query）
 * @returns {Object|null} 返回 { code, codeVerifier, deviceId, state } 或 null（如果验证失败）
 */
export function handleVKCallback(queryParams = {}) {
  try {
    // 提取回调参数（可能从 URL 参数或 payload 中）
    let code, state, deviceId

    // 检查是否有 payload 参数（JSON 格式）
    if (queryParams.payload) {
      try {
        const payload = JSON.parse(decodeURIComponent(queryParams.payload))
        code = payload.code
        state = payload.state
        deviceId = payload.device_id
      } catch (e) {
        // eslint-disable-next-line
        console.error('Failed to parse payload:', e)
        return null
      }
    } else {
      // 直接从 URL 参数获取
      code = queryParams.code
      state = queryParams.state
      deviceId = queryParams.device_id
    }

    if (!code || !state) {
      // eslint-disable-next-line
      console.warn('Missing code or state in callback')
      return null
    }

    // 从 sessionStorage 获取保存的 state 和 code_verifier
    const { state: savedState, codeVerifier } = getVkAuthCache()

    if (!savedState || !codeVerifier) {
      // eslint-disable-next-line
      console.warn('Missing saved state or code_verifier in cache')
      return null
    }

    // 验证 state 是否匹配（防止 CSRF 攻击）
    if (state !== savedState) {
      // eslint-disable-next-line
      console.error('State mismatch: received state does not match saved state')
      // 清理存储的数据
      clearVkAuthCache()
      return null
    }

    // 验证成功，清理 sessionStorage
    removeVkStateCache()
    // 注意：code_verifier 需要保留，直到成功交换 token 后才能删除
    // 但为了安全，如果后续流程失败，也应该清理

    return {
      code,
      codeVerifier,
      deviceId,
      state,
    }
  } catch (error) {
    // eslint-disable-next-line
    console.error('VK callback handling failed:', error)
    // 清理存储的数据
    clearVkAuthCache()
    return null
  }
}

// 旧版代码（已注释，保留作为参考）
// https://dev.vk.com/api/oauth-parameters
// export function initVK(options) {
//   const { gaTrack = true } = options || {}
//   window.location.href = `https://oauth.vk.com/authorize?client_id=8061982&display=popup&redirect_uri=${
//     window.location.origin
//   }/ru/loginCode&response_type=code&openapi=1&scope=email${
//     !gaTrack ? '&gaTrack=false' : ''
//   }`
// }
